import { IDepartment } from '../auth/account-service.types';
import { IApiComponent } from './assignment-service.types';
import { IShiftTaskStatus } from './shift-task-service.data';
import { IApiUserAccount } from './user-service.types';

export type AnalyticsReportResult = any;

export type GetPersonalWorkReportByShiftTaskParams = {
    workerId: number,
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date,
    endToUtc: Date,
}

export type PersonalWorkReportRow = {
    days: {
        [key in string]: number;
    },
    documentKey?: string,
    title?: string,
    totalSpentMin: number,
}

export type GetPersonalWorkReportResult = {
    rows: PersonalWorkReportRow[],
    stat: PersonalWorkReportRow,
}

export type GetPersonalWorkReportByOrderEntityParams = {
    workerId: number,
    departmentId: number,
    startFromUtc: Date,
    endToUtc: Date,
}

export type GetMesTechnologyVariationReportParams = {
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date,
    endToUtc: Date,
}

export type GetOrderEntityPlanVsFactReportParams = {
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date,
    endToUtc: Date,
}

export type GetShiftTaskRecentlyCreatedReportParams = {
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date,
    endToUtc: Date,
    stepInterval: number,
}

export type GetShiftTaskAverageAgeReportParams = {
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date,
    endToUtc: Date,
}

export type ShiftTaskAverageAgeReportResult = {
    date: string,
    timeQuantityAvg: number,
    timeQuantitySum: number,
    tasksQuantity: number,
}

export type GetMesOrderAverageAgeReportParams = {
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date,
    endToUtc: Date,
}

export type MesOrderAverageAgeReportResult = {
    date: string,
    timeQuantityAvg: number,
    timeQuantitySum: number,
    tasksQuantity: number,
}

export type GetShiftTaskCreatedAndResolvedTaskComparisonReportParams = {
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date,
    endToUtc: Date,
}

export type ShiftTaskCreatedAndResolvedTaskComparisonReportResult = {
    date: string,
    createdCount: number,
    completedCount: number,
}

export type GetMesCreatedAndResolvedTaskComparisonReportParams = {
    startFromUtc: Date,
    endToUtc: Date,
}

export type MesCreatedAndResolvedTaskComparisonReportResult = {
    date: string,
    createdCount: number,
    completedCount: number,
}

export type GetShiftTaskUploadingUsersReportParams = {
    workerId: number,
    startFromUtc: Date,
    endToUtc: Date,
}

export type ShiftTaskUploadingUsersRow = {
    date?: Date,
    estimateTimeMs: number,
    issueCount: number,
}

export type ShiftTaskUploadingUsersReportResult = {
    rows: ShiftTaskUploadingUsersRow[],
    stat: ShiftTaskUploadingUsersRow,
}

export type GetShiftTaskTimeSpentReportParams = {
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date,
    endToUtc: Date,
}

export type ShiftTaskTimeSpentReportResult = {
    documentKey: string,
    title: string,
    totalSpentMin: number,
}

export type GetMesTimeSpentReportParams = {
    workerId: number,
    startFromUtc: Date,
    endToUtc: Date,
}

export type MesTimeSpentRow = {
    days: {
        [key in string]: number;
    },
    documentKey?: string,
    title?: string,
    totalSpentMin: number,
}

export type MesTimeSpentReportResult = {
    rows: MesTimeSpentRow[],
    stat: MesTimeSpentRow,
}

export type GetWipForDateReport = {
    date?: Date,
}

type MesOrder = {
    id: number,
    orderNumber: string,
}

export type WipForDateReportResult = {
    amount: number,
    amount_released: number,
    stageId: number,
    route_order: number,
    routeCode: string,
    routePointNumber: string,
    mesOrder: MesOrder,
    component: IApiComponent,
    department: IDepartment,
}

export type GetShiftTaskStatisticOnWorkersReportParams = {
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date,
    endToUtc: Date,
}

export type ShiftTaskStatisticOnWorkersReportRow = {
    documentKey: string,
    title: string,
    totalSpentMin: number,
    workers: {
        [key in string]: number;
    },
}

export type ShiftTaskStatisticOnWorkersReportStat = {
    worker: IApiUserAccount,
}

export type ShiftTaskStatisticOnWorkersReportResult = {
    rows: ShiftTaskStatisticOnWorkersReportRow[],
    stat: ShiftTaskStatisticOnWorkersReportStat[],
}

export type GetShiftTaskPlanVsFactReportParams = {
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date,
    endToUtc: Date,
}

export type ShiftTaskPlanVsFactReportResult = {
    documentKey?: string,
    title?: string,
    spentTimeMin: number,
    estimateTimeMs: number,
    status: IShiftTaskStatus,
}

export enum MaintenanceType {
    Maintenance = 'maintenance',
    Repair = 'repair',
}

export type MaintenanceTypeSpec = {
    name: string,
    code: string,
}

export const MaintenanceTypeWithNameSpec: { [key in MaintenanceType]: Omit<MaintenanceTypeSpec, 'code'> } = {
    [MaintenanceType.Maintenance]: { name: 'Техническое обслуживание' },
    [MaintenanceType.Repair]: { name: 'Ремонт' },
}

export const MaintenanceTypeList: MaintenanceTypeSpec[] = Object.keys(MaintenanceTypeWithNameSpec).map(code => ({
    ...MaintenanceTypeWithNameSpec[code as keyof typeof MaintenanceTypeWithNameSpec],
    code: code as MaintenanceType,
}));

export type GetMaintenanceAndRepairReportParams = {
    manufactories: string[],
    departmentIds: number[],
    startFromUtc: Date | null,
    endToUtc: Date | null,
    maintenanceTypes: string[],
}

export type MaintenanceAndRepairReportRow = {
    id: number,
    description: string,
    maintenanceType: MaintenanceType,
    startFrom: Date,
    endTo: Date,
    inventoryNumber: string,
    machineToolName: string,
    departmentId: number,
    manufactory: string,
    sector: string,
}

export type MesDailyEquipmentLoadReportParams = {
    machineToolIds?: number[],
    manufactories?: string[],
    departmentIds?: number[],
    from: Date,
    to: Date,
}

type DailyEquipmentLoadObj = {
    date: string,
    percent: number,
    seconds: number,
}

export type GetMesDailyEquipmentLoadResult = {
    machineToolId?: number,
    inventoryNumber: string,
    modelName: string,
    sumSeconds: number,
    sumPercent: number,
    data: DailyEquipmentLoadObj[],
    department: IDepartment,
};

export enum GanttChartType {
    SplitByFifteenMinutes = 'splitByFifteenMinute',
    SplitByDay = 'splitByDay',
    SplitByMonth = 'splitByMonth',
    SplitByHour = 'splitByHour',
}

export type TypeSpec = {
    name: string,
    code: string,
    scales: any[],
    format: string,
}

export const GanttChartTypeSpec: { [key in GanttChartType]: Omit<TypeSpec, 'code'> } = {
    [GanttChartType.SplitByMonth]: { name: 'Разбить по месяцам', scales: [{ unit: 'month', step: 1 }], format: 'LLLL yyyy' },
    [GanttChartType.SplitByDay]: { name: 'Разбить по дням', scales: [{ unit: 'month', step: 1 }, { unit: 'day', step: 1 }], format: 'dd MMMM yyyy' },
    [GanttChartType.SplitByHour]: { name: 'Разбить по часам', scales: [{ unit: 'day', step: 1 }, { unit: 'hour', step: 1 }], format: 'dd MMM HH:mm' },
    [GanttChartType.SplitByFifteenMinutes]: { name: 'Разбить по 15 минут', scales: [{ unit: 'day', step: 1 }, { unit: 'minute', step: 15 }], format: 'dd MMM HH:mm' },
}

export const GanttChartTypeList: TypeSpec[] = Object.keys(GanttChartTypeSpec).map(code => ({
    ...GanttChartTypeSpec[code as keyof typeof GanttChartTypeSpec],
    code: code as GanttChartType,
}));

export type MesGanttParams = {
    startFromUtc: Date,
    endToUtc: Date,
}

export type MesGanttChartResult = {
    componentName: string,
    factStartAt?: string,
    id: number,
    measureUnit: string,
    planEndAt: string,
    planStartAt: string,
    plannedAmount: number,
}